/**
 * 서버가 내려주는 serverTime(ISO string)을 기준으로,
 * 클라이언트의 '현재시각(nowMs)'을 안정적으로 제공한다.
 *
 * 핵심:
 * - serverTime을 한 번이라도 받으면, 그 이후 nowMs는 Date.now()에 의존하지 않고
 *   performance.now() (모노토닉)으로 흘러간다.
 * - 따라서 사용자가 기기 시간을 바꾸거나 NTP 보정으로 Date.now()가 점프해도
 *   nowMs는 점프하지 않는다.
 *
 * - 서버 응답이 올 때마다 anchor를 재설정하므로,
 *   백그라운드/슬립 등으로 perf가 느려지는 문제도 자연스럽게 보정된다.
 */

let baseServerNowMs = null   // 마지막으로 관측한 "서버 현재 시각(ms)"
let basePerfMs = null        // 위 serverNowMs를 찍었을 때의 performance.now()

// 디버깅/가드용: 마지막으로 받은 deviceNow와 offset
let lastDeviceNowMs = null
let lastClockOffsetMs = 0

// (선택) 점프 감지 임계값: 이보다 큰 점프가 관측되면 콘솔 경고
const JUMP_WARN_MS = 30 * 1000 // 30초

export function updateOffsetFromResponse (res) {
  const dateStr = res && res.data && res.data.serverTime
  if (!dateStr) return

  const serverNow = new Date(dateStr).getTime()
  if (!Number.isFinite(serverNow)) return

  const perfNow = (typeof performance !== 'undefined' && performance.now)
    ? performance.now()
    : null

  const deviceNow = Date.now()
  lastDeviceNowMs = deviceNow
  lastClockOffsetMs = serverNow - deviceNow

  // 모노토닉 기반 앵커 설정
  if (perfNow != null) {
    // 점프 감지(선택)
    if (baseServerNowMs != null && basePerfMs != null) {
      const predicted = baseServerNowMs + (perfNow - basePerfMs)
      const drift = serverNow - predicted
      if (Math.abs(drift) >= JUMP_WARN_MS) {
        // eslint-disable-next-line no-console
        console.warn('[responseOffset] server time jump detected', {
          driftMs: drift,
          serverNow,
          predicted,
          dateStr
        })
      }
    }

    baseServerNowMs = serverNow
    basePerfMs = perfNow
    return
  }

  // performance.now()가 없는 환경이면 기존 방식 fallback
  baseServerNowMs = serverNow
  basePerfMs = null
}

/**
 * 보정된 현재시각(ms)
 * - serverTime을 받은 적이 있으면: server 기준으로 모노토닉 진행
 * - 받은 적이 없으면: Date.now() fallback
 */
export function nowMs () {
  if (baseServerNowMs == null) return Date.now()

  // perf 앵커가 있으면 모노토닉으로 흘려보냄 (기기 시간 변경 영향 없음)
  if (basePerfMs != null && typeof performance !== 'undefined' && performance.now) {
    const elapsed = performance.now() - basePerfMs
    return baseServerNowMs + elapsed
  }

  // fallback: (아주 구형/특수환경) serverNow와 device offset 기반
  return Date.now() + lastClockOffsetMs
}

/**
 * (디버깅용) 현재 offset 상태를 보고 싶을 때 사용
 */
export function getClockDebug () {
  return {
    baseServerNowMs,
    basePerfMs,
    lastDeviceNowMs,
    lastClockOffsetMs
  }
}